import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { fetchAuthSession } from "aws-amplify/auth";
import { useState } from "react";
import { toast } from "sonner";
import { useShallow } from "zustand/react/shallow";
import { useLoading } from "../../context/LoadingContext";
import { useExportTableToXLSX } from "../../hooks/useExportTableToXLSX";
import { useClinicalEvaluationStore } from "../../store/useClinicalEvaluationStore";
import { getSelectedStudyProductName } from "../../utils/clinicalStudy";
import { checkPipelineLoss, normalizePmids, extractPmid } from "../../utils/clinicalEvalPipeline";
import { buildWsUrl } from "../../utils/wsUrl";
import Table from "../Table";
import { pmidColumn, titleColumn } from "./Columns";
import { RegulifyService } from "../../api/RegulifyService";

const WS_PING_INTERVAL_MS = 20000;

const WS_ERROR_MESSAGE = "There was a problem processing your Clinical Evalulation request. Please try again later.";

const UNHANDELLED_ERROR_MESSAGE = "Unhandelled Error fulfilling the request: "

const WS_URL_DOWNLOAD = (token) =>
  buildWsUrl("download_matching_articles", token);
const WS_URL_EVALUATE = (token) =>
  buildWsUrl("find_clinical_relevancy", token);

const getAuthToken = async () => {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString();
};

const startKeepAlive = (ws) =>
  setInterval(() => {
    if (ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "ping" }));
    }
  }, WS_PING_INTERVAL_MS);

function handleParsedMessage(parsed, { setLoading }) {
  switch (parsed.type) {
    case "error":
      console.error("Processing error:", parsed.message);
      toast.error(`Error: ${parsed.message}`);
      setLoading(false);
      return true;

    case "info":
      console.info("Processing update:", parsed.message);
      toast.info(`Update: ${parsed.message}`);
      return true;

    case "pong":
      console.info("Received ping/pong reply:", parsed.message);
      return true;

    default:
      return false;
  }
}

const columnHelper = createColumnHelper();

const initialColumns = [
  columnHelper.accessor("title", titleColumn),
  columnHelper.accessor((row) => row.pmid, pmidColumn),
];

function hasBalancedParentheses(query) {
  let count = 0;

  for (const char of query) {
    if (char === '(') count++;
    if (char === ')') count--;
    if (count < 0) return false; // closing before opening
  }

  return count === 0; // must end balanced
}

const SearchEval = ({ goToNextTab }) => {
  const { loading, setLoading } = useLoading();
  const { selectedStudy, pipelineStageData, setPipelineStage } =
    useClinicalEvaluationStore(
      useShallow((state) => ({
        selectedStudy: state.selectedStudy,
        pipelineStageData: state.pipelineStageData,
        setPipelineStage: state.setPipelineStage,
      })),
    );
  const [searchTerm, setSearchTerm] = useState();
  const [buttonState, setButtonState] = useState("search");
  const [columns, setColumns] = useState(initialColumns);
  const [data, setData] = useState();
  const [downloadSummary, setDownloadSummary] = useState(null);
  const [sorting, setSorting] = useState([]);
  const exportTableToXLSX = useExportTableToXLSX();
  const studyName = getSelectedStudyProductName(selectedStudy);

  const table = useReactTable({
    data,
    columns,
    enableColumnPinning: true,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: "includesString",
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
    initialState: {
      columnPinning: {
        left: ["title", "pmid"],
        right: [],
      },
    },
  });

  const findClinicalRelevancy = async (code) => {
    try {
      if (!studyName) {
          toast.error("studyName is missing!");
          return;
      }

      setLoading(true, "Finding matching publications… This may take a few minutes. Thank you for waiting.", "text-green-600");
      let keepAliveInterval;
      
      await RegulifyService.get_allowed_credits(5);

      const token = await getAuthToken();
      const ws = new WebSocket(WS_URL_EVALUATE(token));

      ws.binaryType = "arraybuffer";

      ws.onopen = () => {
        keepAliveInterval = startKeepAlive(ws);

        const payload = {
          product_name: studyName,
          search_term: searchTerm,
        };

        ws.send(JSON.stringify(payload));
        toast.info("Processing started...");
      };

      ws.onmessage = (event) => {
        const { data } = event;

        try {
          const parsed = JSON.parse(data);

          if (handleParsedMessage(parsed, { setLoading })) { return; }

          if (parsed) {
            setupData({ data: parsed });
          } else {
            console.log("Unexpected output content received:", parsed);
          }
        } catch {
          console.log("Non-JSON message received:", data);
        }
      };

      ws.onerror = () => {
        console.error(WS_ERROR_MESSAGE);
        toast.error(WS_ERROR_MESSAGE);
        setLoading(false);
      };

      ws.onclose = () => {
        console.log("WebSocket closed");
        clearInterval(keepAliveInterval);
        setLoading(false);
      };
    } catch (error) {
      console.error(UNHANDELLED_ERROR_MESSAGE, error);
      setLoading(false);
    }
  };

  const setupData = (response) => {
    const relevancyResults = response.data.relevancy_results.data;
    const answerResults = response.data.llm_answers_results;
    const currentPmids = normalizePmids(
      (relevancyResults || []).map((item) => extractPmid(item)),
    );
    const prevStage = pipelineStageData?.download;
    if (prevStage?.studyName === studyName) {
      checkPipelineLoss({
        previousStage: "Downloaded Articles",
        currentStage: "Clinical Relevancy",
        previousPmids: prevStage.pmids,
        currentPmids,
      });
    }
    setPipelineStage("relevancy", currentPmids, studyName);

    const dynamicColumns = answerResults[0].responses.map((col, index) =>
      columnHelper.accessor("c-" + index, {
        id: "c-" + index,
        cell: (info) => (
          <div
            className={`${index !== answerResults[0].responses.length - 1 ? "text-center" : ""}`}
          >
            {info.getValue()}
          </div>
        ),
        header: () => (
          <span className="block w-full break-words whitespace-pre-line">
            {col.query}
          </span>
        ),
        size: index === answerResults[0].responses.length - 1 ? 500 : 300,
        meta: {
          className: `${index === answerResults[0].responses.length - 1 ? "max-w-[500px] min-w-[500px]" : "max-w-[300px] min-w-[300px]"}`,
        },
      }),
    );

    const updatedData = data.map((item) => {
      const relevancyResult = relevancyResults.find(
        (relItem) => relItem.pmid === item.pmid,
      );

      const answerResult = answerResults.find(
        (relItem) => relItem.pmid === item.pmid,
      );

      if (relevancyResult) {
        const dataItem = {
          ...item,
          relevance:
            relevancyResult?.llm_decision?.relevant === "no"
              ? "Not relevant"
              : "Relevant",
          reasoning:
            relevancyResult?.llm_decision?.relevant === "no"
              ? relevancyResult?.llm_decision?.reasoning
              : "",
        };
        if (answerResult) {
          answerResult.responses.forEach(
            (res, index) => (dataItem["c-" + index] = res.response),
          );
        }

        return dataItem;
      }
    });
    setData(updatedData);
    setColumns([
      ...columns,
      columnHelper.accessor((row) => row.relevance, {
        id: "relevance",
        cell: (info) => (
          <div
            className={`badge badge-soft w-full text-center ${info.getValue() === "Relevant" ? "badge-success" : "badge-error"}`}
          >
            {info.getValue()}
          </div>
        ),
        header: () => <span>Relevance</span>,
        size: 150,
        meta: { className: "min-w-[150px] max-w-[150px]" },
      }),
      columnHelper.accessor((row) => row.reasoning, {
        id: "reasoning",
        cell: (info) => info.getValue(),
        size: 150,
        meta: { className: "min-w-[150px] max-w-[150px]" },
      }),
      ...dynamicColumns,
    ]);
    setButtonState("next");
  };


  const downloadMatchingArticles = async (code) => {
    try {
      if (!studyName) {
          toast.error("studyName is missing!");
          return;
        }

      if (searchTerm == null || searchTerm === "") {
        toast.warning(`Enter search term first to find matching literature`);
        return;
      }

      if (!hasBalancedParentheses(searchTerm)) {
        toast.error("Invalid search term defined.. checking opening '(' or ')' brackets");
        return;
      }

      setLoading(true, "Evaluating clinical relevancy… This may take a few minutes. Thank you for waiting.", "text-green-600");

      setColumns(initialColumns);
      setDownloadSummary(null);
      setData(undefined);

      let keepAliveInterval;
      
      await RegulifyService.get_allowed_credits(5);

      const token = await getAuthToken();
      const ws = new WebSocket(WS_URL_DOWNLOAD(token));

      ws.binaryType = "arraybuffer";

      ws.onopen = () => {
        keepAliveInterval = startKeepAlive(ws);

        const payload = {
          product_name: studyName,
          search_term: searchTerm,
        };

        ws.send(JSON.stringify(payload));
        toast.info("Processing started...");
      };

      ws.onmessage = (event) => {
        const { data } = event;

        try {
          const parsed = JSON.parse(data);

          if (handleParsedMessage(parsed, { setLoading })) { return; }

          if (parsed) {
            const summary = parsed.download_summary || null;
            const totalKept =
              summary && typeof summary.total_kept === "number"
                ? summary.total_kept
                : summary && summary.total_kept != null
                  ? Number(summary.total_kept)
                  : null;
            const results = Array.isArray(parsed.results) ? parsed.results : [];

            if (summary) {
              setDownloadSummary(summary);
            }

            if (totalKept === 0) {
              const emptyPmids = normalizePmids(
                (parsed.results || []).map((item) => extractPmid(item)),
              );
              setPipelineStage("download", emptyPmids, studyName);
              setData([]);
              setButtonState("search");
              if (summary?.message) {
                toast.error(summary.message);
              }
              return;
            }

            const downloadPmids = normalizePmids(
              results.map((item) => extractPmid(item)),
            );
            setPipelineStage("download", downloadPmids, studyName);
            setData(results);
            const hasResults = totalKept != null ? totalKept > 0 : results.length > 0;
            setButtonState(hasResults ? "eval" : "search");
            if (summary?.message) {
              const showSuccess = totalKept != null ? totalKept > 0 : results.length > 0;
              toast[showSuccess ? "success" : "error"](summary.message);
            }
          } else {
            console.log("Unexpected output content received:", parsed);
          }
        } catch {
          console.log("Non-JSON message received:", data);
        }
      };

      ws.onerror = () => {
        console.error(WS_ERROR_MESSAGE);
        toast.error(WS_ERROR_MESSAGE);
        setLoading(false);
      };

      ws.onclose = () => {
        console.log("WebSocket closed");
        clearInterval(keepAliveInterval);
        setLoading(false);
      };
    } catch (error) {
      console.error(UNHANDELLED_ERROR_MESSAGE, error);
      setLoading(false);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="h-[calc(100vh-180px)] px-4">
        <div className="flex items-end justify-center gap-2">
          <div className="form-control mt-4 max-w-94 min-w-70">
            <label className="label">
              <span className="label-text font-semibold">Search term:</span>
            </label>
            <input
              id="search-term"
              type="text"
              className="input input-bordered w-full"
              placeholder="Enter search term"
              onChange={(e) => setSearchTerm(e.target.value)}
              value={searchTerm}
            />
          </div>
          <button
            className={`btn tooltip tooltip-bottom ${buttonState === "search" ? "btn-primary" : ""}`}
            data-tip="Enter search term and click to find matching clinical literatures (Maximum matching count should not exceed 9500)"
            onClick={downloadMatchingArticles}
            disabled={loading}
          >
            Search publications
          </button>
          {data && data.length > 0 && (
            <input
              value={table.getState().globalFilter}
              className="input input-bordered ml-8 w-full"
              onChange={(e) => table.setGlobalFilter(String(e.target.value))}
              placeholder="Search..."
            />
          )}
          {buttonState === "eval" && (
            <button
              className="btn btn-primary tooltip tooltip-left"
              data-tip="Click to find clinical relevancy for the device in study among below literatures"
              onClick={findClinicalRelevancy}
              disabled={loading}
            >
              Evaluate
            </button>
          )}
          {buttonState !== "search" && (
            <button
              className="btn tooltip tooltip-left"
              data-tip="Download the below data in excel format"
              onClick={() => exportTableToXLSX(data, columns)}
            >
              export
            </button>
          )}
          {buttonState === "next" && (
            <button
              className="btn btn-primary"
              onClick={goToNextTab}
              disabled={loading}
            >
              Next
            </button>
          )}
        </div>
        {downloadSummary && (
          <div className="mt-4 rounded border border-violet-200 bg-violet-50 p-4 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <div className="text-sm font-semibold text-black">Download summary across databases</div>
                <div
                  className={`mt-1 text-base font-semibold ${downloadSummary.total_kept > 0 ? "text-green-700" : "text-red-700"}`}
                >
                  {downloadSummary.message}
                </div>
              </div>
              <div className="flex flex-wrap gap-3 text-sm">
                <div className="rounded-full bg-slate-100 px-3 py-1 text-black">
                  Found: <span className="font-semibold">{downloadSummary.total_found ?? 0}</span>
                </div>
                <div className="rounded-full bg-slate-100 px-3 py-1 text-black">
                  Duplicates: <span className="font-semibold">{downloadSummary.total_duplicates ?? 0}</span>
                </div>
                <div
                  className={`rounded-full px-3 py-1 ${downloadSummary.total_kept > 0 ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}
                >
                  Kept: <span className="font-semibold">{downloadSummary.total_kept ?? 0}</span>
                </div>
              </div>
            </div>
          </div>
        )}
        {data && data.length > 0 && (
          <div className="mt-4 max-h-[calc(100vh-280px)] overflow-x-auto overflow-y-auto">
            <Table table={table} />
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchEval;
